import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'context/AuthProvider';
import {
  finishLaunchExchange,
  getLaunchTokenFromUrl,
  getToken,
  isLaunchExchangeInProgress,
  runLaunchExchangeOnce,
} from 'lib/api';
import { extractRoomCodeFromSearch, savePendingRoomCode } from 'lib/pendingRoomCode';

/** Consumer launch entry — exchanges ?session= launch token for a game session. */
export default function SessionLaunch() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading, exchangeAndLogin } = useAuth();

  const launchToken = useMemo(
    () => getLaunchTokenFromUrl() ?? params.get('session'),
    [params]
  );

  const roomCode = useMemo(
    () => extractRoomCodeFromSearch(params.toString()) ?? params.get('roomCode'),
    [params]
  );

  useEffect(() => {
    if (roomCode) savePendingRoomCode(roomCode);
  }, [roomCode]);

  useEffect(() => {
    if (!launchToken) return;

    void runLaunchExchangeOnce(launchToken, () => exchangeAndLogin(launchToken)).then((ok) => {
      if (ok) {
        const code = roomCode || extractRoomCodeFromSearch(window.location.search);
        if (code) {
          navigate(`/private/${encodeURIComponent(code.trim().toUpperCase())}`, { replace: true });
        } else {
          navigate('/home', { replace: true });
        }
      } else {
        finishLaunchExchange();
        navigate('/session-expired', { replace: true });
      }
    });
  }, [launchToken, roomCode, exchangeAndLogin, navigate]);

  useEffect(() => {
    if (launchToken || getLaunchTokenFromUrl() || isLaunchExchangeInProgress()) return;
    if (loading) return;

    if (user || getToken()) {
      finishLaunchExchange();
      const code = roomCode || extractRoomCodeFromSearch(window.location.search);
      if (code) {
        navigate(`/private/${encodeURIComponent(code.trim().toUpperCase())}`, { replace: true });
      } else {
        navigate('/home', { replace: true });
      }
    } else {
      navigate('/session-expired', { replace: true });
    }
  }, [launchToken, roomCode, loading, user, navigate]);

  return (
    <div className="provider-home flex min-h-screen items-center justify-center">
      <div className="provider-login-panel rounded-2xl p-10 text-center">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-tp-gold border-t-transparent" />
        <p className="mt-4 text-sm font-semibold uppercase tracking-wider text-white/70">Loading…</p>
      </div>
    </div>
  );
}
